/**
 * The one scoped permission read.
 *
 * BMS-spec `docs/edge/rest-api.md` §"Current-user permission projection",
 * paragraphs beginning "Clients MUST fetch this projection..." and "While
 * protected controls are visible online...".
 *
 * Every protected control in both UIs asks this hook, and this hook asks the
 * server. It replaces three hardcoded role lists that transcribed `schema.sql`'s
 * seeded grants and went stale the moment a site regranted a permission.
 *
 * The lifecycle the contract requires, and where each part lives here:
 *
 * | Requirement | Mechanism |
 * |---|---|
 * | Fetch after login/site selection, refresh, reload, resume, site switch | the scope key changes, or `refetchOn*` fires |
 * | Clear stale scope **first** | `removeQueries` on every other scope, in a layout effect |
 * | Reject late responses | scope-keyed cache, plus the response's own `siteCode`/`id` are checked |
 * | Invalidate on 403 and refetch | `use403Invalidation`, once per scope |
 * | Revalidate at least every 30s while visible online | `refetchInterval`, foreground only |
 * | A failed revalidation disables controls until a successful one | `isUnavailable` gates every answer |
 *
 * What this hook is *not*: it is not enforcement. The server resolves live
 * authorization on every mutation, so a revocation takes effect at the API
 * before any display refresh — and an operation already in flight and already
 * authorized is not retroactively undone. This only decides what to offer.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { getApiClient } from '../api/client.js';
import { useAuthStore } from '../store/authStore.js';
import { canOperateLocks as canOperateLocksAt, canWriteAlarms as canWriteAlarmsAt, grantsAt, hasPermissionAt, isProhibitedForIdentity, permissionScopeKey, } from '../types/permissions.js';
/**
 * The contract's upper bound on how stale a visible protected control may be.
 *
 * Not a freshness guarantee — the server is still the authority on every
 * mutation — but the point past which the client must have asked again.
 */
export const VISIBLE_CONTROL_REVALIDATION_MS = 30_000;
/** The root of every projection cache key, for scope-wide invalidation. */
const PROJECTION_KEY_ROOT = ['auth', 'me'];
/**
 * Resolved permissions for one site.
 *
 * Pass `siteCode` to ask about a specific site; omit it to follow the selected
 * site from the auth store; pass `null` to ask for global scope only.
 */
export function usePermissions(siteCode) {
    const queryClient = useQueryClient();
    const identityId = useAuthStore((state) => state.user?.id ?? null);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const selectedSiteCode = useAuthStore((state) => state.currentSiteCode);
    const accessToken = useAuthStore((state) => state.token);
    const scopeSiteCode = siteCode === undefined ? selectedSiteCode : siteCode;
    const queryKey = useMemo(() => permissionScopeKey(identityId, scopeSiteCode), [identityId, scopeSiteCode]);
    const scopeSignature = queryKey.join('');
    // Clear the stale scope *before* the new one is read, not after it arrives.
    // Between a site switch and the new answer there must be no projection at all
    // — a window in which the previous site's grants are still readable is a
    // window in which a control is offered on the wrong site's authority.
    useEffect(() => {
        queryClient.removeQueries({
            queryKey: PROJECTION_KEY_ROOT,
            predicate: (query) => query.queryHash !== JSON.stringify(queryKey),
        });
    }, [queryClient, queryKey, scopeSignature]);
    const query = useQuery({
        queryKey,
        queryFn: async () => {
            const client = getApiClient();
            client.setSiteCodeGetter(() => useAuthStore.getState().currentSiteCode);
            client.setAccessTokenGetter(() => useAuthStore.getState().token);
            client.setAuthFailureHandler(() => useAuthStore.getState().logout());
            const projection = await client.getCurrentUser(scopeSiteCode);
            // A response that answers a different question is not a late-arriving
            // answer to this one. React Query's keying already isolates scopes; this
            // catches the other direction — a server, proxy or cache that ignored the
            // `siteCode` query and replied about something else.
            if (projection.siteCode !== (scopeSiteCode ?? null)) {
                throw new Error('The permission projection answered for a different site');
            }
            if (identityId != null && projection.id !== identityId) {
                throw new Error('The permission projection answered for a different identity');
            }
            return projection;
        },
        enabled: isAuthenticated && identityId != null,
        // No stale window: a protected control asks about now.
        staleTime: 0,
        // Nothing is retained after the last consumer unmounts, so a remount or a
        // return to a previous site re-reads instead of resurrecting an old answer.
        gcTime: 0,
        // The visible-control bound. Foreground only: a backgrounded tab shows no
        // control that needs revalidating, and polling it would be pure noise.
        refetchInterval: VISIBLE_CONTROL_REVALIDATION_MS,
        refetchIntervalInBackground: false,
        // Reload, foreground resume, and reconnection.
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        refetchOnReconnect: 'always',
        // A failed read disables controls; it does not queue retries that would
        // make the disable-until-success rule depend on retry timing.
        retry: false,
    });
    use403Invalidation(query.error, queryKey, scopeSignature, queryClient, query.refetch);
    useRefetchOnTokenChange(accessToken, query.refetch);
    const projection = query.isSuccess ? query.data : null;
    // Everything below fails closed. `grantsAt` returns null rather than [] for a
    // scope it cannot answer, so "no grants here" and "I do not know" stay apart.
    const covered = grantsAt(projection, scopeSiteCode, identityId) !== null;
    return useMemo(() => ({
        projection: covered ? projection : null,
        siteCode: scopeSiteCode ?? null,
        isLoading: query.isPending,
        isUnavailable: !covered,
        has: (permission) => covered && hasPermissionAt(projection, scopeSiteCode, permission, identityId),
        canWriteAlarms: covered && canWriteAlarmsAt(projection, scopeSiteCode),
        canOperateLocks: covered && canOperateLocksAt(projection, scopeSiteCode),
        identitySource: covered ? projection.identitySource : null,
        isProhibited: (operation) => isProhibitedForIdentity(operation, covered ? projection.identitySource : null),
        refetch: () => { void query.refetch(); },
    }), [covered, projection, scopeSiteCode, identityId, query.isPending, query.refetch]);
}
/**
 * Read the projection again when the access token is replaced.
 *
 * The contract lists token refresh alongside login, reload, resume and site
 * switch. The scope key deliberately does **not** include the token — a
 * credential has no business in a cache key, and the answer is about the
 * identity and the site, not about which token carried the question — so the
 * refetch is driven from the change instead.
 *
 * The first token is skipped: installing it is what enabled the initial read.
 */
function useRefetchOnTokenChange(accessToken, refetch) {
    const previousToken = useRef(null);
    useEffect(() => {
        const previous = previousToken.current;
        previousToken.current = accessToken;
        if (previous === null || accessToken === null || previous === accessToken)
            return;
        void refetch();
    }, [accessToken, refetch]);
}
/**
 * Invalidate the projection on `403` and read it again — exactly once.
 *
 * A 403 means this identity's authority at this scope is not what the cached
 * answer said, so the cached answer is dropped immediately rather than left to
 * expire. The refetch is deliberately one-shot per scope: a 403 is often the
 * *stable* truth ("you hold no assignment at this site"), and refetching on
 * every 403 would spin. The 30-second revalidation still runs underneath.
 */
function use403Invalidation(error, queryKey, scopeSignature, queryClient, refetch) {
    const handledScope = useRef(null);
    useEffect(() => {
        if (handledScope.current !== scopeSignature)
            handledScope.current = null;
        const status = error?.response?.status;
        if (status !== 403)
            return;
        if (handledScope.current === scopeSignature)
            return;
        handledScope.current = scopeSignature;
        queryClient.setQueryData(queryKey, undefined);
        void refetch();
    }, [error, queryClient, queryKey, scopeSignature, refetch]);
}
//# sourceMappingURL=usePermissions.js.map